function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import tokens from '@veneer/tokens';
import position from 'polished/lib/shorthands/position';
import typography from '../utils/typography';
import { useTheme } from '@veneer/theme';
var useStyles = _ref => {
  var {
    appearance,
    direction,
    expanded,
    focusVisible,
    interactive,
    status
  } = _ref;
  var theme = useTheme();
  return React.useMemo(() => {
    var isHorizontal = direction === 'horizontal';
    var isHc = theme.mode === 'contrast';
    var getHcNotInteractiveColor = color => !interactive && isHc ? theme.palette.foreground.default : color;
    var labelColor = () => {
      if (isHc && !interactive && status !== 'disabled') return theme.palette.foreground.default;
      if (status === 'current' || status === 'complete') return theme.palette.primary.default;
      if (status === 'incomplete') return theme.palette.neutral.default;
      return theme.palette.foreground.disabled;
    };
    var interactionLabelColor = interaction => {
      if (status === 'complete') return theme.palette.primary[interaction];
      if (status === 'incomplete') return theme.palette.neutral[interaction];
      return 'undefined';
    };
    var stepForegroundColor = () => {
      if (status === 'complete') return theme.palette.foreground.inverse;
      if (isHc && !interactive && status !== 'disabled') return theme.palette.foreground.default;
      if (status === 'current') return theme.palette.primary.default;
      if (status === 'incomplete') return theme.palette.neutral.default;
      return theme.palette.foreground.disabled;
    };
    var interactionForeground = interaction => {
      if (status === 'incomplete') {
        if (isHc && interactive) return theme.palette.foreground.inverse;
        return theme.palette.neutral[interaction];
      }
      return 'undefined';
    };
    var stepBackgroundColor = () => {
      if (status === 'current' || status === 'incomplete') return 'transparent';
      if (status === 'complete') return getHcNotInteractiveColor(theme.palette.primary.default);
      if (status === 'disabled' && appearance === 'compact') return theme.palette.stroke.default;
      return theme.palette.background.disabled;
    };
    var interactionBackground = (interaction, color) => {
      if (status === 'complete') return theme.palette.primary[interaction];
      if (status === 'incomplete') {
        if (isHc && interactive) return theme.palette.neutral[interaction];
        return theme.palette.neutral[color];
      }
      return 'undefined';
    };
    var stepBorderColor = () => {
      var border = (width, color) => "".concat(width, " solid ").concat(getHcNotInteractiveColor(theme.palette[color].default));
      if (status === 'current') return border('2px', 'primary');
      if (status === 'incomplete') return border('1px', 'neutral');
      return '1px solid transparent';
    };
    var interactionBorder = interaction => {
      if (status === 'incomplete') return "1px solid ".concat(getHcNotInteractiveColor(theme.palette.neutral[interaction]));
      return 'undefined';
    };
    var barBackgroundColor = () => {
      if (status === 'complete' || status === 'current') return getHcNotInteractiveColor(theme.palette.primary.default);
      return theme.palette.stroke.default;
    };
    var barMargin = () => {
      if (isHorizontal && appearance === 'standard') return "calc(".concat(tokens.layout.size4, " + 1px) ").concat(tokens.layout.size3, " 0 ").concat(tokens.layout.size3);
      if (isHorizontal && appearance === 'compact') return "calc(".concat(tokens.layout.size2, " - 1px) ").concat(tokens.layout.size3, " 0 ").concat(tokens.layout.size3);
      if (!isHorizontal && appearance === 'standard') return "".concat(tokens.layout.size3, " calc(").concat(tokens.layout.size4, " + 1px)");
      return "".concat(tokens.layout.size3, " calc(").concat(tokens.layout.size2, " - 1px)");
    };
    var marginBottomValues = () => {
      if (!isHorizontal) return 0;
      if (isHorizontal && appearance === 'compact') return tokens.layout.size2;
      return tokens.layout.size3;
    };
    var marginRightValues = () => {
      if (isHorizontal) return 0;
      if (!isHorizontal && appearance === 'compact') return tokens.layout.size2;
      return tokens.layout.size3;
    };
    var stepSize = appearance === 'standard' ? "calc(".concat(tokens.layout.size1, " + ").concat(tokens.layout.size8, ")") : tokens.layout.size4;
    var verticalLabelAlignment = appearance === 'compact' ? "calc(".concat(stepSize, " + ").concat(theme.layout.size2, ")") : "calc(".concat(stepSize, " + ").concat(theme.layout.size3, ")");
    return {
      stepContainer: _objectSpread(_objectSpread({
        alignItems: 'center',
        color: labelColor(),
        cursor: interactive && status !== 'disabled' && status !== 'current' ? 'pointer' : 'default',
        display: !isHorizontal ? 'flex' : undefined,
        flexDirection: isHorizontal ? 'column' : 'row'
      }, typography(theme).label), {}, {
        position: 'relative'
      }, interactive && {
        '&:hover:not(:active) > .step': {
          backgroundColor: interactionBackground('hover', 'backgroundLight'),
          border: interactionBorder('hover'),
          color: interactionForeground('hover')
        },
        '&:hover > .step': {
          backgroundColor: interactionBackground('active', 'background'),
          border: interactionBorder('active'),
          color: interactionForeground('active')
        },
        '&:hover:not(:active)': {
          color: interactionLabelColor('hover')
        },
        '&:hover': {
          color: interactionLabelColor('active')
        }
      }),
      step: _objectSpread(_objectSpread({
        boxSizing: 'border-box',
        alignItems: 'center',
        backgroundColor: stepBackgroundColor(),
        border: stepBorderColor(),
        borderRadius: tokens.layout.size6,
        color: stepForegroundColor(),
        display: 'flex'
      }, typography(theme).label), {}, {
        justifyContent: 'center',
        marginBottom: marginBottomValues(),
        marginRight: marginRightValues(),
        maxHeight: stepSize,
        maxWidth: stepSize,
        minHeight: stepSize,
        minWidth: stepSize,
        position: 'relative',
        '&::after': interactive && _objectSpread(_objectSpread({
          border: "2px solid ".concat(theme.palette.foreground.default),
          borderRadius: appearance === 'standard' ? "calc(".concat(theme.borderRadius5, " + 5px)") : theme.borderRadius,
          content: "' '",
          opacity: 0,
          transition: 'all 50ms'
        }, position('absolute', 0)), focusVisible && _objectSpread({
          borderRadius: appearance === 'standard' ? "calc(".concat(theme.borderRadius5, " + 5px)") : "calc(".concat(theme.borderRadius, " + 2px)"),
          opacity: 1
        }, position('-4px')))
      }),
      bar: {
        backgroundColor: barBackgroundColor(),
        borderRadius: '1px',
        margin: barMargin(),
        minHeight: !isHorizontal ? "calc(".concat(tokens.layout.size5, " + ").concat(tokens.layout.size6, ")") : undefined,
        minWidth: isHorizontal ? "calc(3 * ".concat(theme.layout.size8, ")") : undefined,
        width: !isHorizontal ? '2px' : expanded ? '100%' : undefined,
        height: isHorizontal ? '2px' : expanded ? '100%' : undefined
      },
      label: {
        left: isHorizontal ? '50%' : verticalLabelAlignment,
        position: 'absolute',
        textAlign: isHorizontal ? 'center' : undefined,
        transform: isHorizontal ? 'translateX(-50%)' : undefined,
        width: isHorizontal ? '120px' : undefined
      }
    };
  }, [appearance, direction, expanded, focusVisible, interactive, status, theme]);
};
export default useStyles;